import dayjs from "dayjs";
import React, { Component } from "react";
import { reportException } from "./utils/sentry";

// Lazy: keeps googleAnalytics (and its storageService/utils graph) out of the
// entry chunk. Crash events are rare; the module arrives well before the
// user can do anything else, and dataLayer queues the event regardless.
const fireAnalyticsEvent = (...args) => {
  import("./utils/googleAnalytics").then(({ fireGtmEvent }) =>
    fireGtmEvent(...args),
  );
};

class ErrorBoundry extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, info) {
    fireAnalyticsEvent("page_crashed", { error, info, errMsg: error?.message });
    reportException(error, info);
    console.error("ErrorBoundary caught an error", error, info);

    // Skip auto-reload for bots/prerender
    if (typeof window !== 'undefined' && window.navigator && /bot|crawler|spider|prerender/i.test(window.navigator.userAgent)) {
      this.setState({ hasError: true });
      return;
    }

    const lastReloaded = typeof localStorage !== 'undefined' ? localStorage.getItem("last_error_reloaded") : null;
    if (
      !lastReloaded ||
      dayjs(lastReloaded).isBefore(dayjs().subtract(10, "minutes"))
    ) {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem("last_error_reloaded", dayjs().toISOString());
      }
      this.handleReload();
    }
    this.setState({
      hasError: true,
    });
  }

  handleReload = () => {
    window.location.reload(true);
    fireAnalyticsEvent("errorboundary_reloaded");
  };

  render() {
    if (this.state.hasError) {
      // For bots, render empty div to allow React Helmet meta tags to stay
      const isBot = typeof window !== 'undefined' && window.navigator && /bot|crawler|spider|prerender/i.test(window.navigator.userAgent);

      if (isBot) {
        // Return minimal content for bots - React Helmet tags will still be in head
        return (
          <div style={{padding: '20px'}}>
            <h1>Loading...</h1>
            <p>Please wait while we load the content.</p>
          </div>
        );
      }

      // You can render any custom fallback UI for regular users
      return (
        <div className="errorBoundary">
          <h1>Something went wrong.</h1>
          <p>
            We're sorry for the inconvenience. Please try refreshing the page or
            reopening the tab.
          </p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

export const SentryErrorBoundary = ({ children }) => {
  return <ErrorBoundry>{children}</ErrorBoundry>;
};

export default ErrorBoundry;
